import { round } from "lodash";
import { DateFilterOptions, ChartItem, ChartData } from "./ChartUtil";
import { WorkingTimeChartData } from "./charts/TimeWorkedChart";
import { CaseCountChartData } from "./charts/CaseCountChart";

/*
  Metrics Totals Calculations
  Sum all the cases or working times so they can be used to compute metrics.
  "Production" means "services cases completed"

  Aggregations
  - productionData and workingTimeData are:
    - Hourly aggregations if dateValue is "1"
    - Daily aggregations if dateValue is "7" or greater
  - todayProductionData and todayWorkingTimeData are always hourly aggregations.
  
  Units
  - The production data's timeWorked property has units of seconds.
  - workingTime and idleTime properties have units of minutes.
  - calculateTotalWorkingTime and calculateTotalIdleTime convert both of these
    and return hours.

  Summation Logic
  - If looking at today's chart, use the hourly data from today.
  - If looking at a multi-day chart, sum daily data for all except today.
    Then add the hourly data for today.

  Considerations for Skill Filters
  When querying case production data, we are able to filter by skill.
  When querying working time data, we cannot filter by skill because that
  property is not stored in user activity reports.
  - Therefore, if skill filters are currently applied, we must use the
    production data's timeWorked property in lieu of the working time data.
*/

export const calculateTotalNumberOfCases = (
  dateValue: DateFilterOptions,
  productionData: ChartItem[],
  todayProductionData: ChartItem[],
  skillFiltered: boolean
): number => {
  /*
    When we query case production data, we get the latest data.
    When we query working time data, we get the most recently hourly report.
    For example, if we query at 9:30am, we get:
    - Production data up to 9:30
    - Working time data up to 9:00
    In order to keep both data sets in sync, we truncate the last partial hour
    of the production data.

    Special case: Within 5 min past the hour
    The UserActivityHourlyRollupJob which generates hourly reports runs
    every 5 min past the hour. Therefore if we query at 9:04am, we get:
    - Production data up to 9:04
    - Working time data up to 8:00
    Therefore we need to truncate an additional hour of the production data.
  */
  const isWithinFiveMinPastTheHour = new Date().getMinutes() <= 5;
  const todayProduction = todayProductionData.reduce((agg, val, idx) => {
    if (idx === todayProductionData.length - 1 && !skillFiltered) {
      return agg;
    }
    if (idx === todayProductionData.length - 2 && isWithinFiveMinPastTheHour && !skillFiltered) {
      return agg;
    }
    return agg + val.count;
  }, 0);
  if (dateValue === "1") {
    return todayProduction;
  }
  return productionData.reduce(
    (agg, val, idx) => (idx === productionData.length - 1 && !skillFiltered ? agg + todayProduction : agg + val.count),
    0
  );
};

export const calculateTotalWorkingTime = (
  dateValue: DateFilterOptions,
  todayProductionData: ChartItem[],
  productionData: ChartItem[],
  todayWorkingTimeData: ChartItem[],
  workingTimeData: ChartItem[],
  skillFiltered: boolean
): number => {
  if (skillFiltered) {
    if (dateValue === "1") {
      return todayProductionData.reduce((agg, val) => agg + (val.aggregations?.timeWorked || 0), 0) / 3600; // seconds -> hours
    } else {
      return productionData.reduce((agg, val) => agg + (val.aggregations?.timeWorked || 0), 0) / 3600;
    }
  }

  const todayWorked = todayWorkingTimeData.reduce((agg, val) => agg + (val.aggregations?.workingTime || 0), 0);
  if (dateValue === "1") {
    return todayWorked / 60;
  }

  const totalWorkedExceptToday = workingTimeData
    .slice(0, -1)
    .reduce((agg, val) => agg + (val.aggregations?.workingTime || 0), 0);
  return (totalWorkedExceptToday + todayWorked) / 60; // minutes -> hours
};

export const calculateTotalIdleTime = (
  dateValue: DateFilterOptions,
  todayWorkingTimeData: ChartItem[],
  workingTimeData: ChartItem[],
  skillFiltered: boolean
): number => {
  // Service cases don't track idle time, so we have no data.
  if (skillFiltered) {
    return 0;
  }

  const todayIdle = todayWorkingTimeData.reduce((agg, val) => agg + (val.aggregations?.idleTime || 0), 0);
  if (dateValue === "1") {
    return todayIdle / 60;
  }

  const totalIdleExceptToday = workingTimeData
    .slice(0, -1)
    .reduce((agg, val) => agg + (val.aggregations?.idleTime || 0), 0);
  return (totalIdleExceptToday + todayIdle) / 60;
};

/*
  Data Merging and QPH Calculation
  Merge multiple data sources that will be presented on the same chart.
  - Case Count chart is a grouped bar chart showing workedCases and incomingCases.
  - Time Worked chart contains:
    - Stacked bar chart showing idleTime on top of workingTime
    - Line chart showing QPH, overlaid on top
*/

export const mergeCaseCountDataForChart = (incoming: ChartData, production: ChartData, working: ChartData) => {
  const data: CaseCountChartData[] = [];
  if (incoming && production) {
    /*
      Choose the longest array of the 3, so both charts have the same x-axis width.
      We only care about the datetime label (`name`) in the longest array
      because it's the piece of data that all 3 arrays have in common, and can
      therefore be used for lookups.
    */
    const axisData = [incoming, production, working].reduce((max, curr) => (curr.length > max.length ? curr : max), []);
    axisData.forEach((axisDataPoint) => {
      const productionDataPoint = production.find((d) => d.name === axisDataPoint.name);
      const incomingDataPoint = incoming.find((d) => d.name === axisDataPoint.name);
      const dataPoint: CaseCountChartData = {
        name: axisDataPoint.name,
        workedCases: productionDataPoint?.count || 0,
        incomingCases: incomingDataPoint?.count || 0,
      };
      data.push(dataPoint);
    });
  }
  return data;
};

export const mergeTimeWorkedDataForChart = (
  incoming: ChartData,
  todayProduction: ChartData,
  production: ChartData,
  todayWorking: ChartData,
  working: ChartData,
  skillFiltered: boolean,
  dateValue: DateFilterOptions
) => {
  const data: WorkingTimeChartData[] = [];
  if (working && todayWorking && production) {
    const axisData = [incoming, production, working].reduce((max, curr) => (curr.length > max.length ? curr : max), []);
    axisData.forEach((axisDataPoint) => {
      const dataPoint: WorkingTimeChartData = { name: axisDataPoint.name, workingTime: 0, idleTime: 0, qph: 0 };
      if (skillFiltered) {
        const productionDataPoint = production.find((d) => d.name === axisDataPoint.name);
        dataPoint.workingTime = (productionDataPoint?.aggregations?.timeWorked || 0) / 3600;
      } else {
        const workingDataPoint = working.find((d) => d.name === axisDataPoint.name);
        dataPoint.workingTime = (workingDataPoint?.aggregations?.workingTime || 0) / 60;
        dataPoint.idleTime = (workingDataPoint?.aggregations?.idleTime || 0) / 60;
      }
      data.push(dataPoint);
    });

    // Use hourly reports for last day
    if (dateValue !== "1" && data.length > 0) {
      const lastDay = data[data.length - 1];
      if (skillFiltered) {
        lastDay.workingTime = todayProduction.reduce((agg, val) => agg + (val.aggregations?.timeWorked || 0), 0) / 3600;
        lastDay.idleTime = 0;
      } else {
        lastDay.workingTime = todayWorking.reduce((agg, val) => agg + (val.aggregations?.workingTime || 0), 0) / 60;
        lastDay.idleTime = todayWorking.reduce((agg, val) => agg + (val.aggregations?.idleTime || 0), 0) / 60;
      }
    }

    // Round values for all data points
    data.forEach((dataPoint, index) => {
      const productionDataPoint = production.find((d) => d.name === dataPoint.name);
      const casesCompleted = round(productionDataPoint?.count || 0, 1);
      const workingTime = round(dataPoint.workingTime || 0, 1);
      const idleTime = round(dataPoint.idleTime || 0, 1);

      // Calculate QPH using rounded numbers, to prevent a small denominator
      // from causing an abnormally large QPH.
      let qph = 0;
      if (workingTime > 0 && casesCompleted >= 0) {
        qph = round(casesCompleted / workingTime, 1);
      }

      data[index] = { ...dataPoint, workingTime, idleTime, qph };
    });
  }

  return data;
};
